import PropTypes from 'prop-types';
import React from 'react';
import ReviewResponse from '../../../components/Community/ReviewResponse/ReviewResponse';
import { imgSrcPrefix } from '../../../components/Includes/variables';
import { Consumer as PageConsumer } from '../../../contexts/PageContext';
import { checkPermission } from '../../../utilities/helperFunctions';
import { truncateWithEllipsis } from '../../../utilities/ui_utilities';
import { EmitMetric } from '../../Analytics/VisibilityPixel/VisibilityPixel';
import SliderChart from '../../Charts/SliderChart/SliderChart';
import UserDisplay from '../../Content/User/UserDisplay/UserDisplay';
import FGBLabel from '../../Elements/FGBLabel/FGBLabel';
import PopUp from '../../Elements/PopUp/PopUp';
import { SINGLE_REVIEW_PROPTYPES } from '../../Includes/SingleReviewProps';
import Button from '../../Input/Button/Button';
import { buildImagePath } from '../../JobComponents/Company/Image/logo';
import Rating from '../../JobV3/Elements/Rating/Rating';
import QuestionResponseCard from './QuestionResponseCard/QuestionResponseCard';
import { EMPLOYER_ROLES } from '../../../pages/constants';
import './SingleReviewComponent.scss';

export const SINGLE_REVIEW = 'single-review';
export const MULTIPLE_REVIEW = 'multiple-review';

export default class SingleReviewComponent extends React.PureComponent {
    constructor(props) {
        super();
        this.state = {
            expanded: props.expanded || false,
            leaveReviewPopup: false,
        };
    }

    static propTypes = {
        ...SINGLE_REVIEW_PROPTYPES,
        company: PropTypes.shape({
            companyName: PropTypes.string,
            companyId: PropTypes.number,
            logo: PropTypes.shape({
                src: PropTypes.string,
                alt: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            }),
            companyUrl: PropTypes.string,
            rating: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        }).isRequired,
        expanded: PropTypes.bool,
        location: PropTypes.oneOf([SINGLE_REVIEW, MULTIPLE_REVIEW]),
        employerResponse: PropTypes.shape({
            jobTitle: PropTypes.string,
            comment: PropTypes.string,
            date: PropTypes.string,
        }),
        metric: PropTypes.object,
    };

    static defaultProps = {
        location: SINGLE_REVIEW,
    };

    renderResponseCards = () => {
        return this.props.reviewDetails.map((response, idx) => {
            const content = (
                <div>
                    {response.barChar ? (
                        <SliderChart rating={response.barChar} />
                    ) : null}
                    {response.response ? (
                        <div className="responseText">{response.response}</div>
                    ) : null}
                    {response.extraInfo ? (
                        <div className="responseText">{response.extraInfo}</div>
                    ) : null}
                    {response.comment ? (
                        <div className="responseText">{response.comment}</div>
                    ) : null}
                </div>
            );
            return (
                <QuestionResponseCard
                    key={idx}
                    class={'bottom-border'}
                    title={response.headline}
                >
                    {content}
                </QuestionResponseCard>
            );
        });
    };

    expand = () => {
        this.setState({
            expanded: true,
        });
    };

    loadButton = () => {
        return this.props.location === SINGLE_REVIEW ? (
            <div className="btnDiv expanded">
                <a
                    href={`/company-reviews/${this.props.company.companyEndpoint}`}
                >
                    <Button
                        type={'solid'}
                        value={'See More Reviews'}
                        onClick={this.buttonRedirect}
                    />
                </a>
            </div>
        ) : null;
    };

    openLeaveReviewPopup = () => {
        this.setState({
            leaveReviewPopup: true,
        });
    };

    pageRedirect = () => {
        window.location.href = '/company/leave-review';
    };

    renderLeaveReviewPopup = (context) => {
        const injectedTransforms = {
            small: 'c_scale,f_auto,q_auto,w_93,h_100',
        };
        const imgSrc = buildImagePath(
            '/v1536676783/HomePage/Icons/icon_intel.png',
            'small',
            injectedTransforms
        );

        return (
            <PopUp
                showPopUp={this.state.leaveReviewPopup}
                showLogo={false}
                showOverlay={true}
                cancel={this.pageRedirect}
                close={this.pageRedirect}
            >
                <div className="popup-content">
                    <img className="image" src={imgSrc} />
                    <FGBLabel>Get Unlimited Access To Job Reviews</FGBLabel>
                    <div className="description">
                        To be fair to women who've reviewed their employer,
                        please contribute your own anonymous review.
                    </div>
                    <div className="get-started-button">
                        <Button
                            type={'solid'}
                            value={'Get started'}
                            onClick={() => {
                                this.setState(
                                    { leaveReviewPopup: false },
                                    context.toggleJobReviewSurvey(true)
                                );
                            }}
                        />
                    </div>
                </div>
            </PopUp>
        );
    };
    renderResponseIndicator = () => {
        return this.props.employerResponse ? (
            <div className="response-indicator all-caps-header">1 Response</div>
        ) : null;
    };
    getUserRolesForEmployer = (session) => {
        const companyRoles =
            (session.userCompaniesRoles &&
                session.userCompaniesRoles[this.props.company.companyId]) ||
            [];
        return session.additionalRoles
            ? companyRoles.concat(session.additionalRoles)
            : companyRoles;
    };
    renderReviewResponse = ({ session }) => {
        const allRoles = this.getUserRolesForEmployer(session);
        return (
            <ReviewResponse
                employerResponse={this.props.employerResponse}
                companyName={this.props.company.companyName}
                companyId={this.props.company.companyId}
                logo={this.props.company.logo}
                userRoles={allRoles}
                reviewId={this.props.reviewId}
            />
        );
    };

    renderGoogleSeo = () => {
        return this.props.googleSeo ? (
            <div
                className="google-seo"
                dangerouslySetInnerHTML={this.convertGoogleSeoString()}
            />
        ) : null;
    };

    convertGoogleSeoString = () => {
        const content =
            "<script type='application/ld+json'>" +
            this.props.googleSeo +
            '</script>';
        return { __html: content };
    };
    isExpanded = () => this.props.expanded || this.state.expanded;

    renderButton = (context) => {
        if (this.isExpanded()) {
            return this.loadButton();
        }

        let onClickHandler = null;
        if (context.isLoggedIn()) {
            onClickHandler = this.openLeaveReviewPopup;
            const session = context.pageProps.session;
            const authorizedRoles = [
                EMPLOYER_ROLES.reviewer,
                EMPLOYER_ROLES.admin,
                'superadmin',
                'fullEditor',
                'customerSuccess',
            ];
            if (
                session &&
                (session.leftReview ||
                    checkPermission(
                        this.getUserRolesForEmployer(session),
                        authorizedRoles
                    ))
            ) {
                onClickHandler = this.expand;
            }
        }

        return context.wrapAuth(
            'SingleReview',
            <div className="btnDiv compact">
                <Button
                    type={'solid'}
                    value={
                        context.isLoggedIn()
                            ? 'See full review >'
                            : 'Login to access the full review >'
                    }
                    onClick={() => {
                        if (this.props.metric) {
                            EmitMetric(this.props.metric);
                        }
                        onClickHandler();
                    }}
                />
            </div>,
            { login_trigger: 'read-single-review' }
        );
    };

    render() {
        const location = this.props.locationName
            ? `, ${this.props.locationName}`
            : '';
        const customText = (
            <span>
                {this.props.user.username} shared this review of{' '}
                <a className="companyLink" href={this.props.company.companyUrl}>
                    {this.props.company.companyName}
                </a>
                {location} on {this.props.date}
            </span>
        );
        return (
            <PageConsumer>
                {(pageContext) => (
                    <div className="component-Community-SingleReviewComponent">
                        {this.renderResponseIndicator()}
                        <UserDisplay
                            image={this.props.user.image}
                            imageSize={36}
                            customText={customText}
                        />
                        <div className="review">
                            {' '}
                            {this.isExpanded()
                                ? `"${this.props.comment}"`
                                : `"${truncateWithEllipsis(
                                      this.props.comment,
                                      60
                                  )}"`}
                        </div>
                        <div className="rating-section">
                            <div className="rating-title">
                                Overall Job Satisfaction:
                            </div>
                            <div className="rating-display">
                                <div className="ratingText">
                                    {this.props.rating}
                                </div>
                                <Rating
                                    showNumber={false}
                                    rating={this.props.rating}
                                    size={'medium'}
                                />
                            </div>
                        </div>
                        <div className="reviewCards">
                            {!this.props.expanded && !this.state.expanded ? (
                                <QuestionResponseCard title="Are women & men treated equally?">
                                    <img
                                        className="blurredImage smallScreen"
                                        src={`${imgSrcPrefix}/c_scale,w_260/v1536240819/prod/community/BLUR_image_mobile.png`}
                                    />
                                    <img
                                        className="blurredImage largeScreen"
                                        src={`${imgSrcPrefix}/v1536240819/prod/community/BLUR_image_desktop.png`}
                                    />
                                </QuestionResponseCard>
                            ) : null}
                            {this.props.reviewDetails && this.isExpanded()
                                ? this.renderResponseCards()
                                : null}
                        </div>
                        {this.props.reviewDetails && this.isExpanded()
                            ? this.renderReviewResponse(pageContext.pageProps)
                            : null}
                        {this.renderButton(pageContext)}
                        {this.renderLeaveReviewPopup(pageContext)}
                        {this.renderGoogleSeo()}
                    </div>
                )}
            </PageConsumer>
        );
    }
}
